import type {ReactElement} from 'react';
import React from 'react';
import {ContextualMenuMediaControlOptions} from '@Components/contextual-menu/components/contextual-menu-media-control-options';
import type {MediaControlsItemProps} from '@Components/contextual-menu/components/media-controls-item';
import {openCropperModal} from '@Modals/cropper-modal/cropper-modal.library';
import type {CropperPanelApplyResponse} from '@Panels/cropper-panel';
import {getCurrentPageBackground} from '@Components/poster-editor/poster-editor-reducer';
import type {ReplaceMediaControlsButtons} from '@Components/replace-media-controls';
import {ReplaceMediaControls} from '@Components/replace-media-controls';
import {onOpenReplaceBackground} from '@Components/poster-editor/library/poster-editor-open-modals';
import {useAppSelector} from '@/hooks';
import styles from './background-image-media-controls.module.scss';
import {BackgroundTypeName, isImageBackground} from "@PosterWhiteboard/page/background/background.types";

export function BackgroundImageMediaControls(): ReactElement<any> | null {
  const currentPageBackground = useAppSelector(getCurrentPageBackground);

  if (!currentPageBackground || currentPageBackground.details.type !== BackgroundTypeName.IMAGE) {
    return null;
  }

  const getItems = (): MediaControlsItemProps[] => {
    return [
      {
        id: 'background-image-crop',
        className: styles.cropOption,
        icon: 'icon-crop',
        text: window.i18next.t('pmwjs_crop'),
        onClick: onOpenCrop,
      },
      {
        id: 'background-image-replace',
        icon: 'icon-replace',
        text: window.i18next.t('pmwjs_replace'),
        className: styles.cropOption,
        dropdownContent: (
          <ReplaceMediaControls
            onClick={(id: ReplaceMediaControlsButtons): void => {
              onOpenReplaceBackground(id);
            }}
          />
        ),
      },
    ];
  };

  const onCropCallback = (response: CropperPanelApplyResponse): void => {
    const currentPage = window.posterEditor?.whiteboard?.getCurrentPage();
    if (!currentPage) {
      return;
    }
    void currentPage.background.updateFromObject({
      details: {
        cropData: {
          cropped: true,
          ...response.cropData,
        },
      },
    });
  };

  const onOpenCrop = (): void => {
    const poster = window.posterEditor?.whiteboard?.pages.poster;
    if (!poster) {
      return;
    }

    const backgroundItem = poster.getCurrentPage().background.details;
    if (!isImageBackground(backgroundItem)) {
      return;
    }

    openCropperModal({
      onApply: onCropCallback,
      imageHashedFilename: backgroundItem.hashedFilename,
      imageSource: backgroundItem.imageSource,
      dataUrl: backgroundItem.uploadingImageData?.dataUrl,
      showRemoveBackgroundOption: false,
      initialCropData: backgroundItem.cropData,
      showCancelButton: false,
      showCroppingPresets: false,
      aspectRatio: poster.width / poster.height,
    });
  };

  return <ContextualMenuMediaControlOptions id="background-image-media-controls" items={getItems()} />;
}
